import type { ScenarioId } from "@/contexts/DemoContext";

export type DemoOverlayType =
  | "fullscreen"
  | "thought-bubble"
  | "arrow"
  | "reveal-modal";

export interface FullscreenOverlayData {
  /**
   * The string fields below contain i18n keys (resolved at render time by
   * the DemoOverlayController via useTranslation). `factsKey` is an i18n
   * key whose value must be an array of strings (resolved with returnObjects).
   */
  employeeThoughtKey?: string;
  managerThoughtKey: string;
  revealLabelKey: string;
  revealTitleKey: string;
  ctaLabelKey: string;
  factsKey?: string;
  /** Optional illustration displayed centered at the top of the overlay. */
  illustrationUrl?: string;
}

export interface DemoStepConfig {
  /** Route the app should be on for this step. */
  route: string;
  /** Short human-readable title for this step. */
  title: string;
  /** Which kind of overlay the controller should render. */
  overlayType: DemoOverlayType;
  /** Data for fullscreen overlays. */
  fullscreen?: FullscreenOverlayData;
}

export interface DemoScenarioConfig {
  id: ScenarioId;
  steps: DemoStepConfig[];
}

/**
 * Configuration des scénarios de démo. Pour le scénario 1 (la fonceuse),
 * seule l'étape 1 est définie pour l'instant — les autres seront ajoutées
 * au fur et à mesure de la construction du parcours.
 */
export const DEMO_SCENARIOS: Record<ScenarioId, DemoScenarioConfig> = {
  scenario_1: {
    id: "scenario_1",
    steps: [
      {
        route: "/rencontres-employes",
        title: "Le contexte",
        overlayType: "fullscreen",
        fullscreen: {
          employeeThoughtKey: "demoV3.overlay.scenario_1.employeeThought",
          managerThoughtKey: "demoV3.overlay.scenario_1.managerThought",
          revealLabelKey: "demoV3.overlay.labels.hiddenStake",
          revealTitleKey: "demoV3.overlay.scenario_1.revealTitle",
          ctaLabelKey: "demoV3.overlay.scenario_1.ctaLabel",
        },
      },
      {
        route: "/rencontres-employes",
        title: "Préparer la rencontre",
        overlayType: "arrow",
      },
    ],
  },
  scenario_2: {
    id: "scenario_2",
    steps: [],
  },
  scenario_3: {
    id: "scenario_3",
    steps: [
      {
        route: "/rencontres-employes",
        title: "Le doute",
        overlayType: "fullscreen",
        fullscreen: {
          managerThoughtKey: "demoV3.overlay.scenario_3.managerThought",
          factsKey: "demoV3.overlay.scenario_3.facts",
          revealLabelKey: "demoV3.overlay.labels.hiddenStake",
          revealTitleKey: "demoV3.overlay.scenario_3.revealTitle",
          ctaLabelKey: "demoV3.overlay.scenario_3.ctaLabel",
        },
      },
    ],
  },
};

export function getDemoStep(
  scenario: ScenarioId | null,
  stepIndex: number,
): DemoStepConfig | null {
  if (!scenario) return null;
  const config = DEMO_SCENARIOS[scenario];
  return config?.steps[stepIndex] ?? null;
}